import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Text,
} from "@chakra-ui/react";
import { FormikProps, withFormik } from "formik";
import { Button, IconButton } from "@chakra-ui/button";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import { TextField } from "../components/fields/TextField/TextField";
import { FormStack } from "../components/FormStack/FormStack";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import * as Yup from "yup";
import { ApiResult, DataApiClient } from "../api/dataApiClient";
import { CopyText } from "../components/Utilities/CopyText";
import { PlusIcon } from "../styles/icons";
import { DeleteIcon } from "@chakra-ui/icons";
import { cloneDeep } from "lodash";
import { ErrorMessage } from "../components/ErrorMessage/ErrorMessage";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { CherryHubDataApi } from "../api/dataApiModels";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

const TwilioAccountSchema = Yup.object().shape({
  AccountId: Yup.string().required("Account Id is a required field"),
  SmsSenderId: Yup.string()
    .required("SMS Sender Id is a required field")
    .matches(/^[a-zA-Z0-9 +]{0,12}$/, {
      message:
        "Invalid SMS SenderId, only allow alphanumeric and space with maximum 12 characters",
      excludeEmptyString: false,
    }),
});

const validationSchema = Yup.object().shape({
  DefaultAccount: TwilioAccountSchema,
  ServiceName: Yup.string().required("Service Name is a required field"),
  SubAccounts: Yup.array().of(TwilioAccountSchema),
});

const subAccountView = (
  subAccounts: CherryHubDataApi.TwilioAccount[] | null | undefined,
  account: CherryHubDataApi.TwilioAccount,
  accountIdx: number,
  fieldName: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => (
  <AccordionItem
    w="100%"
    key={accountIdx}
    className={`sub-account-${accountIdx}`}
  >
    {({ isExpanded }) => (
      <>
        <h2>
          <AccordionButton textAlign="left" gap={4}>
            <Box flexShrink={1}>
              <b>{`Sub Account ${accountIdx + 1}`}</b>
            </Box>
            <Box flexGrow={1}>
              <IconButton
                aria-label="Delete sub account."
                title="Delete sub account."
                icon={<DeleteIcon />}
                size="xs"
                bgColor="cherryGrey.160"
                onClick={() => {
                  subAccounts?.splice(accountIdx, 1);
                  setFieldValue("SubAccounts", subAccounts);
                }}
              />
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel width="100%">
          {isExpanded && (
            <VStack w="100%" alignItems="flex-start">
              <TextField
                horizontal
                label="Account Id"
                name={`${fieldName}.AccountId`}
              />

              <TextField
                horizontal
                label="SMS Sender Id"
                name={`${fieldName}.SmsSenderId`}
              />
            </VStack>
          )}
        </AccordionPanel>
      </>
    )}
  </AccordionItem>
);

interface TwilioConfigurationFormValues
  extends CherryHubDataApi.TwilioConfigurationUpdate {}

interface TwilioConfigurationFormProps {
  apiClient: DataApiClient;
  twilioConfiguration: CherryHubDataApi.TwilioConfiguration | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (integration: CherryHubDataApi.TwilioConfiguration) => void;
  onFailure: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  values,
  errors,
  twilioConfiguration,
  setFieldValue,
}: TwilioConfigurationFormProps &
  FormikProps<TwilioConfigurationFormValues>) => {
  const { data: serviceNameOptions, isLoading: serviceNameOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.ServiceName),
      []
    );

  const addSubAccount = (scrollTo?: boolean) => {
    if (!values.SubAccounts) {
      values.SubAccounts = [];
    }
    values.SubAccounts!.push({ AccountId: "", SmsSenderId: "" });
    setFieldValue("SubAccounts", values.SubAccounts);
    if (scrollTo)
      setTimeout(
        () =>
          document
            .querySelector(`.sub-account-${values.SubAccounts?.length! - 1}`)
            ?.scrollIntoView({ behavior: "smooth" }),
        500
      );
  };

  return (
    <>
      <Box width="100%" alignSelf="flex-start">
        <FormStack>
          {twilioConfiguration && (
            <HStack w="100%">
              <Box flexGrow={1} pb={4}>
                <CopyText
                  text={twilioConfiguration?.Meta?.Id!}
                  hover
                  adjustMargin
                >
                  <Text fontSize="md" color="cherryUi.600">
                    {twilioConfiguration?.id}
                  </Text>
                </CopyText>
              </Box>

              <Box>
                <Button
                  colorScheme="cherryButton"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Box>

              <Box>
                <Button disabled={isSubmitting} onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </HStack>
          )}

          <VStack
            w="100%"
            maxW="1200px"
            alignItems="flex-start"
            spacing={4}
            pb={6}
          >
            <ChakraReactSelectField
              horizontal
              label="Service Name"
              name="ServiceName"
              selectedOptionColor="blue"
              isLoading={serviceNameOptionsLoading}
              options={
                serviceNameOptions?.map((option) => {
                  return {
                    label: option.Value!,
                    value: option.Key!,
                  };
                }) ?? []
              }
            />

            <TextField
              horizontal
              label="Parent Account Id"
              name="ParentAccountId"
            />

            <TextField
              horizontal
              label="Default Account Id"
              name="DefaultAccount.AccountId"
            />

            <TextField
              horizontal
              label="Default SMS Sender Id"
              name="DefaultAccount.SmsSenderId"
            />

            {(values.SubAccounts?.length ?? 0) == 0 && (
              <VStack w="100%" p={4} spacing={2}>
                <Text fontStyle="italic" fontSize="sm">
                  Sub Accounts has not been set.
                </Text>
                <Button
                  size="sm"
                  leftIcon={<PlusIcon />}
                  onClick={() => addSubAccount()}
                  colorScheme="cherryButton"
                >
                  Sub Account
                </Button>
              </VStack>
            )}

            {(values.SubAccounts?.length ?? 0) > 0 && (
              <>
                <Button
                  leftIcon={<PlusIcon />}
                  onClick={() => addSubAccount(true)}
                  colorScheme="cherryButton"
                  alignSelf="flex-end"
                  size="sm"
                >
                  Sub Account
                </Button>

                <Accordion
                  w="100%"
                  allowMultiple
                  allowToggle
                  variant="custom-field"
                >
                  <VStack w="100%">
                    {values.SubAccounts?.length &&
                      values.SubAccounts?.map((account, accountIdx) =>
                        subAccountView(
                          values.SubAccounts,
                          account,
                          accountIdx,
                          `SubAccounts[${accountIdx}]`,
                          setFieldValue
                        )
                      )}
                  </VStack>
                </Accordion>
              </>
            )}
          </VStack>

          {!isValid && (
            <ErrorMessage>One or more validation has failed.</ErrorMessage>
          )}

          <HStack width="100%" justifyContent="center" spacing="3" pt="8">
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const TwilioConfigurationForm = withFormik<
  TwilioConfigurationFormProps,
  TwilioConfigurationFormValues
>({
  mapPropsToValues: ({ twilioConfiguration }) => {
    const result = cloneDeep(twilioConfiguration);
    return {
      ParentAccountId: result?.ParentAccountId ?? "",
      DefaultAccount: {
        AccountId: result?.DefaultAccount?.AccountId ?? "",
        SmsSenderId: result?.DefaultAccount?.SmsSenderId ?? "",
      },
      ServiceName: result?.ServiceName ?? "DataAPI",
      SubAccounts: result?.SubAccounts,
    };
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    let result: ApiResult<CherryHubDataApi.TwilioConfiguration> | null = null;

    if (props.twilioConfiguration == null) {
      result = await props.apiClient.createTwilioConfiguration(values);
    } else {
      result = await props.apiClient.updateTwilioConfiguration(
        props.twilioConfiguration.Meta?.Id!,
        values
      );
    }

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,
  validationSchema,
})(InnerForm);
